'use client';

import React from 'react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, PlusIcon, PencilIcon } from '@heroicons/react/20/solid';

interface SelectOption {
  value: string | number;
  label: string | React.ReactNode;
}

interface SelectWithAddProps {
  label?: string | React.ReactNode;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  value: string | number;
  onChange: (value: string | number) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  // Add New functionality
  showAddNew?: boolean;
  addNewLabel?: string;
  onAddNew?: () => void; // Callback to open modal
  // Edit functionality
  onEdit?: (value: string | number, label: string | React.ReactNode) => void; // Callback to edit item
  systemValues?: (string | number)[]; // Values that cannot be edited (system items)
}

export default function SelectWithAdd({
  label,
  error,
  helperText,
  id,
  required,
  disabled,
  className = '',
  options,
  value,
  onChange,
  showAddNew = false,
  addNewLabel = 'Add New...',
  onAddNew,
  onEdit,
  systemValues = [],
}: SelectWithAddProps) {
  const { darkMode } = useDarkMode();

  // Generate a unique ID if not provided (using React.useId for SSR compatibility)
  const generatedId = React.useId();
  const selectId = id || generatedId;
  const selectedOption = options.find((opt) => opt.value === value) || options[0];

  return (
    <div className={className}>
      <Listbox as="div" value={value} onChange={onChange} disabled={disabled}>
        {label && (
          <label
            htmlFor={selectId}
            className={`mb-2 block text-[13px] font-medium ${
              darkMode ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
        )}

        <div className="relative">
          <ListboxButton
            id={selectId}
            className={`block grid w-full cursor-default grid-cols-1 rounded-md px-3 py-1.5 text-left text-base outline-1 -outline-offset-1 transition-all sm:text-sm/6 ${
              darkMode
                ? 'bg-white/5 text-gray-100 outline-white/10'
                : 'bg-white text-gray-900 outline-gray-300'
            } ${
              darkMode
                ? 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500'
                : 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600'
            } ${
              error
                ? darkMode
                  ? 'outline-red-500/50 focus:outline-red-500'
                  : 'outline-red-500 focus:outline-red-500'
                : ''
            } ${disabled ? 'disabled:cursor-not-allowed disabled:opacity-60' : ''} `}
          >
            <span className="col-start-1 row-start-1 flex items-center pr-6">
              <span className="block truncate">{selectedOption?.label}</span>
            </span>
            <ChevronUpDownIcon
              aria-hidden="true"
              className={`col-start-1 row-start-1 size-5 self-center justify-self-end ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            />
          </ListboxButton>

          <ListboxOptions
            transition
            anchor="bottom start"
            className={`z-[200] mt-1 max-h-56 w-[var(--button-width)] overflow-auto rounded-md py-1 text-base shadow-lg ring-1 transition duration-100 ease-in focus:outline-hidden data-closed:opacity-0 sm:text-sm ${darkMode ? 'bg-gray-800 ring-white/10' : 'bg-white ring-black/5'} `}
          >
            {/* Regular Options */}
            {options.map((option) => {
              const isSystemItem = systemValues.includes(option.value);
              const canEdit = onEdit && !isSystemItem;

              return (
                <ListboxOption
                  key={option.value}
                  value={option.value}
                  className={`group relative cursor-default py-2 pl-9 select-none data-focus:outline-hidden ${canEdit ? 'pr-12' : 'pr-3'} ${
                    darkMode
                      ? 'text-gray-100 data-focus:bg-indigo-600 data-focus:text-white'
                      : 'text-gray-900 data-focus:bg-indigo-600 data-focus:text-white'
                  } `}
                >
                  {/* Check Icon - Left Side */}
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600 group-not-data-selected:hidden group-data-focus:text-white">
                    <CheckIcon aria-hidden="true" className="size-5" />
                  </span>

                  <span className="block truncate font-normal group-data-selected:font-semibold">
                    {option.label}
                  </span>

                  {/* Edit Button - Right Side */}
                  {canEdit && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onEdit(option.value, option.label);
                        }}
                        className={`rounded p-1 transition-colors ${
                          darkMode
                            ? 'text-gray-400 group-data-focus:text-white hover:bg-indigo-500/10 hover:text-indigo-400 group-data-focus:hover:bg-white/10 group-data-focus:hover:text-white'
                            : 'text-gray-500 group-data-focus:text-white hover:bg-indigo-50 hover:text-indigo-600 group-data-focus:hover:bg-white/10 group-data-focus:hover:text-white'
                        } `}
                        aria-label={`Edit ${option.label}`}
                      >
                        <PencilIcon className="size-4" />
                      </button>
                    </span>
                  )}
                </ListboxOption>
              );
            })}

            {/* Separator */}
            {showAddNew && options.length > 0 && (
              <div className={`my-1 h-px ${darkMode ? 'bg-white/10' : 'bg-gray-200'}`} />
            )}

            {/* Add New Option */}
            {showAddNew && onAddNew && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onAddNew();
                }}
                className={`flex w-full cursor-pointer items-center gap-2 py-2 pr-9 pl-3 text-left transition-colors ${
                  darkMode
                    ? 'text-gray-100 hover:bg-indigo-600 hover:text-white focus:bg-indigo-600 focus:text-white'
                    : 'text-gray-900 hover:bg-indigo-600 hover:text-white focus:bg-indigo-600 focus:text-white'
                } `}
                aria-label={`Create new ${label?.toString().toLowerCase() || 'item'}`}
              >
                <PlusIcon className="size-5" aria-hidden="true" />
                <span className="font-medium">{addNewLabel}</span>
              </button>
            )}
          </ListboxOptions>
        </div>
      </Listbox>

      {(error || helperText) && (
        <p
          className={`mt-2 text-sm ${
            error ? 'text-red-500' : darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
}
